import { createContext, Fragment, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { TextOverrideProvider, useLanguage } from "@/i18n/LanguageContext";
import type { Locale } from "@/i18n/config";
import { localizeContent, type PageContent } from "@/lib/pages/schema";
import { useLivePage } from "@/lib/pages/live";
import { BlockList } from "./BlockRenderer";
import { HOME_SECTIONS, renderNative } from "./nativeRegistry";
import { BlockView, hasCustomStyle, nativeStyleClasses } from "./BlockRenderer";
import { applyMediaOverrides } from "./mediaOverrides";

interface Ctx { routeKey: string; content: PageContent; localized: PageContent; preview: boolean }
const ManagedPageContext = createContext<Ctx | null>(null);
export const useManagedPage = () => useContext(ManagedPageContext);

/**
 * Provides an approved (or previewed) revision to a native page. Without content the children render
 * exactly as built (no wrapper element), so pages are unchanged until an Administrator publishes a revision.
 */
export function ManagedPageView({ routeKey, content, forceLocale, preview = false, children }: {
  routeKey: string; content: PageContent | null; forceLocale?: Locale; preview?: boolean; children: ReactNode;
}) {
  const { language } = useLanguage();
  const locale = forceLocale ?? language;
  const value = useMemo(() => (content ? { routeKey, content, localized: localizeContent(content, locale), preview } : null), [routeKey, content, locale, preview]);
  const scope = useRef<HTMLDivElement>(null);
  const media = content?.media_overrides;
  // Image overrides apply only inside this page/preview and are fully restored on change or unmount.
  useEffect(() => applyMediaOverrides(scope.current, media), [media]);
  return (
    <ManagedPageContext.Provider value={value}>
      <div ref={scope} data-managed-page={routeKey} style={{ display: "contents" }}>
        <TextOverrideProvider overrides={content?.text_overrides} forceLocale={forceLocale}>{children}</TextOverrideProvider>
      </div>
    </ManagedPageContext.Provider>
  );
}

export function ManagedPage({ routeKey, children }: { routeKey: string; children: ReactNode }) {
  const { data } = useLivePage("native", routeKey);
  return <ManagedPageView routeKey={routeKey} content={data?.content ?? null}>{children}</ManagedPageView>;
}

/** Custom blocks placed before ("start") or after ("end") the locked page body of a native page. */
export function ManagedSlot({ at }: { at: "start" | "end" }) {
  const ctx = useManagedPage();
  if (!ctx || ctx.routeKey === "home") return null;
  const blocks = ctx.localized.blocks;
  const idx = blocks.findIndex((b) => b.type === "native" && b.props.component === "page_body");
  const part = idx < 0 ? (at === "start" ? blocks : []) : at === "start" ? blocks.slice(0, idx) : blocks.slice(idx + 1);
  return <BlockList blocks={part} />;
}

/** The built-in page body: hidden when the approved revision replaces it, wrapped when the revision styles it. */
export function ManagedBody({ children }: { children: ReactNode }) {
  const ctx = useManagedPage();
  const body = ctx?.localized.blocks.find((b) => b.type === "native" && b.props.component === "page_body");
  if (body && body.visible === false) return null;
  if (body && hasCustomStyle(body.style)) return <div data-block-id={body.id} className={nativeStyleClasses(body.style)}>{children}</div>;
  return <>{children}</>;
}

/** SEO overrides from the approved revision (current locale). */
export function useManagedSeo() {
  return useManagedPage()?.localized.seo ?? null;
}

/** Home sections: built-in order when nothing is live; otherwise the revision's order/visibility/styles and extra blocks. */
export function ManagedHomeSections() {
  const ctx = useManagedPage();
  if (!ctx) return <>{HOME_SECTIONS.map(([id, C]) => <C key={id} />)}</>;
  return (
    <>
      {ctx.localized.blocks.filter((b) => b.visible !== false).map((b) =>
        b.type === "native" ? <Fragment key={b.id}>{renderNative(b)}</Fragment> : <BlockView key={b.id} block={b} />,
      )}
    </>
  );
}
